import { type ApolloClient } from '@apollo/client'
import { type GetStaticPropsContext, type NextPageWithLayout } from 'next'
import React from 'react'
import { EXPERT_DETAIL_TAB_KEYS } from 'shared-constants'
import { getPublicLayout } from '@public/components/PublicLayout'
import { REVALIDATE_INTERVAL_IN_SECONDS } from 'constants/common'
import { withGA4Provider } from 'hoc/withGoogleAnalytics'
import { GA4_AREAS } from 'providers/googleAnalytics'
import { prepareNavigationProps } from 'routes/utils/prepareGetStaticNavigationProps'
import { PublicExpertDetailConsultationTabDocument } from 'types/graphql-generated'
import { ExpertDetailTabWrapper } from '../ExpertDetailTabWrapper'
import { prepareGetExpertStaticProps } from '../Showroom/prepareGetExpertStaticProps'
import { expertTabsGetStaticProps } from '../utils'
import { ConsultationsTabContent, EXPERT_REVIEWS_LIMIT } from './Content'

const ExpertConsultationsDetailPage: NextPageWithLayout<{
  slug: string
  expertId: number
}> = ({ slug, expertId }) => (
  <ExpertDetailTabWrapper
    slug={slug}
    expertId={expertId}
    currentTab={EXPERT_DETAIL_TAB_KEYS.CONSULTATION}
  >
    <ConsultationsTabContent slug={slug} />
  </ExpertDetailTabWrapper>
)

const consultationTabSpecificGetStaticProps = async (
  context: GetStaticPropsContext,
  { expertId, breadcrumbs, slug },
  apolloClient: ApolloClient<any>
) => {
  const expertApolloClient = await prepareGetExpertStaticProps(
    context,
    { expertId },
    apolloClient
  )

  const navigation = await prepareNavigationProps()
  await expertApolloClient.query({
    query: PublicExpertDetailConsultationTabDocument,
    variables: {
      slug,
      reviewsInput: {
        isPublicVisible: true,
        pagination: {
          page: 1,
          limit: EXPERT_REVIEWS_LIMIT,
        },
      },
    },
  })

  return {
    props: {
      key: `${context.params.slug}-consultations-tab-page`,
      expertId,
      slug,
      navigation,
      breadcrumbs,
      initialApolloState: apolloClient.cache.extract(),
    },
    revalidate: REVALIDATE_INTERVAL_IN_SECONDS,
  }
}

export function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export function getStaticProps(context: GetStaticPropsContext) {
  return expertTabsGetStaticProps(context, {
    currentTab: EXPERT_DETAIL_TAB_KEYS.CONSULTATION,
    callback: consultationTabSpecificGetStaticProps,
  })
}

const ExpertConsultationsDetail = withGA4Provider({
  area: GA4_AREAS.CONSULTATIONS,
})(ExpertConsultationsDetailPage)

ExpertConsultationsDetail.getLayout = (page) =>
  getPublicLayout(page, { isWithBreadcrumbs: true })

export default ExpertConsultationsDetail
